import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2020 semantics GmbH
//  Date      : $Date: 2025-11-27 17:29:05 +0100 (Thu, 27 Nov 2025) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 158738 $
//  Purpose   : dashboard part for tree/id and general dashboard panels
// =============================================================================
// ---Third-party imports
import React, { createElement } from 'react';
// ---Framework imports
import log from 'uix/logging';
import { uniq, sorted, flatten } from 'ldash';
import { useApi, useMemo } from 'uix/hooks';
import { useTreeNodes } from 'uix/db/hooks';
import { TreeNode } from 'uix/types/components';
import { formatDateTime } from 'uix/utils/format';
// ---Globals
export const TreeParentHistory = ({ otid })=>{
    let { data, loading } = useApi('tree/getParentHistory', {
        otid
    });
    let otids = useMemo(()=>{
        if (!data?.treeParentHistory) return [];
        let { treeParentHistory } = data;
        let move_ops = [
            ...treeParentHistory.moved,
            ...treeParentHistory.childmoved
        ];
        return uniq([
            otid,
            ...flatten(move_ops.map((e)=>[
                    e.parent_old,
                    e.parent_new,
                    e.otid
                ])).filter(Boolean)
        ]);
    }, [
        data
    ]);
    let otid2node = useTreeNodes(otids);
    let moved = data?.treeParentHistory.moved || [];
    let build_from_to = (op)=>{
        let old_parent_node = '[ROOT]';
        if (op.parent_old) {
            let node_data = otid2node[op.parent_old];
            old_parent_node = node_data ? /*#__PURE__*/ _jsx(TreeNode, {
                linkLazy: true,
                node: node_data
            }) : op.parent_old;
        }
        let new_parent_node = '[ROOT]';
        if (op.parent_new) {
            let node_data = otid2node[op.parent_new];
            new_parent_node = node_data ? /*#__PURE__*/ _jsx(TreeNode, {
                linkLazy: true,
                node: node_data
            }) : op.parent_new;
        }
        return /*#__PURE__*/ _jsxs("span", {
            children: [
                "from ",
                old_parent_node,
                " to ",
                new_parent_node
            ]
        });
    };
    let build_ts_user = (op)=>{
        return /*#__PURE__*/ _jsxs("span", {
            title: `User: ${op.puid}`,
            children: [
                formatDateTime(op.ts),
                ": "
            ]
        });
    };
    let movedEls = sorted(moved, (e)=>e.ts, true).map((op)=>/*#__PURE__*/ _jsxs("li", {
            children: [
                build_ts_user(op),
                " ",
                build_from_to(op)
            ]
        }, `${op.parent_old}_${op.parent_new}`));
    let childmoved = data?.treeParentHistory.childmoved || [];
    let childmovedEls = sorted(childmoved, (e)=>e.ts, true).map((op)=>{
        let moved_node = otid2node[op.otid] ? /*#__PURE__*/ _jsx(TreeNode, {
            node: otid2node[op.otid]
        }) : op.otid;
        return /*#__PURE__*/ _jsxs("li", {
            children: [
                build_ts_user(op),
                " ",
                moved_node,
                " ",
                build_from_to(op)
            ]
        }, `${op.otid}_${op.parent_old}_${op.parent_new}`);
    });
    let currentNode = otid2node[otid] ? /*#__PURE__*/ _jsx(TreeNode, {
        node: otid2node[otid]
    }) : null;
    let noOps = /*#__PURE__*/ _jsx("div", {
        children: "Keine aufgezeichneten Operationen"
    });
    if (loading) return '...';
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsxs("h2", {
                children: [
                    "Moved ",
                    currentNode,
                    " "
                ]
            }),
            /*#__PURE__*/ _jsx("ul", {
                children: movedEls
            }),
            movedEls.length == 0 && noOps,
            /*#__PURE__*/ _jsx("h2", {
                children: "Moved Children"
            }),
            /*#__PURE__*/ _jsx("ul", {
                children: childmovedEls
            }),
            childmovedEls.length == 0 && noOps
        ]
    });
};
